import React from 'react';
import {
    Alert,
    Box,
    Chip,
    Divider,
    Drawer,
    FormControlLabel,
    IconButton,
    Paper,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import dayjs from 'dayjs';
import { formatFloat, DATE_FORMAT } from '../../../../../helpers';
import { CoverageLine, CoveragePedido, CoverageState } from './coverage';

// What the plan currently does with a product — computed by the page, because
// only it knows the live rows.
export interface ProductPlanState {
    // Machine names already producing this product in the plan on screen.
    machines: string[];
    // Whether a click can add it right now, and if not, why (shown to the user —
    // a disabled control with no reason is the thing this avoids).
    canAdd: boolean;
    reason: string | null;
}

// Nothing planned and nothing addable — the shape a product falls back to when
// the map has no entry for it (a line with no product id).
const NO_PLAN_STATE: ProductPlanState = {
    machines: [],
    canAdd: false,
    reason: null,
};

interface IPedidosDrawer {
    open: boolean;
    onClose: () => void;
    pedidos: CoveragePedido[];
    // Off by default: the drawer opens on committed (En producción) pedidos
    // only. Turning it on brings the Pendientes back — without it, products
    // wanted solely by Pendiente pedidos are invisible here.
    includePending: boolean;
    onIncludePendingChange: (include: boolean) => void;
    // Precomputed per product, NOT a callback. Resolving it per line meant
    // scanning the product catalogue and every row × machine once for each of
    // ~150 lines — and since the drawer is designed to stay open while the plan
    // is edited behind it, that ran on every keystroke.
    productPlanStates: Map<number, ProductPlanState>;
    plannedKilosByProduct: Map<number, number>;
    productionNeededByProduct: Map<number, number>;
    // True when getProductsInventory returns a real balance for the product's
    // production type (see the shared isInventoryTracked helper). Distinguishes
    // "no stock" from "unknown" — pellet/lavado are untracked and read "sin dato".
    isInventoryTracked: (productId: number | null) => boolean;
    // Some planned product is still waiting for its machine x product corridas,
    // so the "previsto este turno" column does not yet include everything the plan will
    // make. Said out loud instead of letting the figures move under the planner.
    ratesPending: boolean;
    canEdit: boolean;
    onAdd: (productId: number) => void;
}

const STATE_LABEL: Record<CoverageState, string> = {
    complete: 'entregado',
    stock: 'cubierto con inventario',
    plan: 'cubierto con lo previsto este turno',
    partial: 'parcial',
    none: 'sin cubrir',
    unmeasured: 'sin equivalencia en kg',
};

// Golden yellow for the `partial` chip instead of MUI's orange `warning`: the
// orange read too close to the red `none` chip. Kept in sync with Pedidos por
// surtir's partial dot (COVERAGE_PARTIAL_COLOR in planning-order-card.tsx).
const COVERAGE_PARTIAL_COLOR = '#eab308';

const STATE_COLOR: Record<
    CoverageState,
    'default' | 'success' | 'info' | 'warning' | 'error'
> = {
    complete: 'default',
    stock: 'success',
    // Deliberately a different colour from `stock`: "this turno is why it's
    // covered" is the signal the whole drawer exists to give.
    plan: 'info',
    partial: 'warning',
    none: 'error',
    // Not a coverage verdict at all — the line could not be measured. Neutral
    // rather than red, because nothing is known to be missing.
    unmeasured: 'default',
};

// Why a line reads "sin equivalencia en kg", spelled out where the planner sees
// it: the number is missing upstream, not here.
const UNMEASURED_TOOLTIP =
    'El pedido pide bultos pero la línea no tiene kilos equivalentes, ' +
    'así que no se puede repartir inventario ni convertirla en horas. ' +
    'Revisa el peso por bulto del producto.';

// Both units on every quantity — the pedido carries both natively, so neither
// needs converting. The line's own unit leads and the other follows in
// parentheses, so a bultos pedido reads as bultos without hiding the kilos the
// hours are actually derived from.
function quantity(line: CoverageLine, kilos: number, groups: number): string {
    return line.unit === 'bultos'
        ? `${formatFloat(groups, 0)} bultos (${formatFloat(kilos, 0)} kg)`
        : `${formatFloat(kilos, 0)} kg (${formatFloat(groups, 0)} bultos)`;
}

function groupsForKilos(line: CoverageLine, kilos: number): number {
    if (line.remaining_kilos <= 0 || line.remaining_groups <= 0) return 0;
    return (kilos / line.remaining_kilos) * line.remaining_groups;
}

function PedidoCard({
    pedido,
    productPlanStates,
    plannedKilosByProduct,
    productionNeededByProduct,
    isInventoryTracked,
    canEdit,
    onAdd,
}: {
    pedido: CoveragePedido;
    productPlanStates: Map<number, ProductPlanState>;
    plannedKilosByProduct: Map<number, number>;
    productionNeededByProduct: Map<number, number>;
    isInventoryTracked: (productId: number | null) => boolean;
    canEdit: boolean;
    onAdd: (productId: number) => void;
}) {
    const deliveryDate = pedido.order_request_estimated_delivery_date
        ? dayjs(pedido.order_request_estimated_delivery_date).format(
              DATE_FORMAT,
          )
        : null;

    return (
        <Paper variant={'outlined'} sx={{ p: 1.5, mb: 1.5 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 1,
                }}
            >
                <Typography variant={'subtitle2'}>
                    {pedido.order_code
                        ? `#${pedido.order_code}`
                        : `Pedido ${pedido.order_request_id}`}
                </Typography>
                <Typography variant={'body2'} sx={{ flexGrow: 1 }}>
                    {pedido.account_name}
                </Typography>
                <Chip
                    size={'small'}
                    variant={'outlined'}
                    label={pedido.order_request_status_name}
                />
            </Box>
            {deliveryDate ? (
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    sx={{ display: 'block', mb: 1 }}
                >
                    {`Entrega estimada: ${deliveryDate}`}
                </Typography>
            ) : null}

            {pedido.lines.map((line) => {
                const planState =
                    (line.product_id
                        ? productPlanStates.get(line.product_id)
                        : null) || NO_PLAN_STATE;
                const tracked = isInventoryTracked(line.product_id);
                const plannedKilos = line.product_id
                    ? plannedKilosByProduct.get(line.product_id) || 0
                    : 0;
                const plannedGroups = groupsForKilos(line, plannedKilos);
                const productionNeededKilos = line.product_id
                    ? productionNeededByProduct.get(line.product_id) || 0
                    : 0;
                const productionNeededGroups = groupsForKilos(
                    line,
                    productionNeededKilos,
                );
                const showAdd =
                    canEdit &&
                    line.state !== 'complete' &&
                    (line.uncovered_kilos > 0 || productionNeededKilos > 0);
                const inventorySummary =
                    tracked
                        ? `Inventario ${quantity(
                              line,
                              line.inventory_kilos,
                              line.inventory_groups,
                          )}${
                              line.inventory_kilos_committed > 0 ||
                              line.inventory_groups_committed > 0
                                  ? ` · Comprometidos ${quantity(
                                        line,
                                        line.inventory_kilos_committed,
                                        line.inventory_groups_committed,
                                    )}`
                                  : ''
                          }`
                        : null;
                const realSummary =
                    line.real_kilos > 0 || line.real_groups > 0
                        ? `Real ${quantity(
                              line,
                              line.real_kilos,
                              line.real_groups,
                        )}`
                        : null;
                const plannedSummary =
                    plannedKilos > line.from_plan_kilos + 0.001
                        ? `Producción planeada total ${quantity(
                              line,
                              plannedKilos,
                              plannedGroups,
                          )}`
                        : null;
                const productionNeededSummary =
                    productionNeededKilos > line.uncovered_kilos + 0.001
                        ? `faltan por producir ${quantity(
                              line,
                              productionNeededKilos,
                              productionNeededGroups,
                          )} (incluye déficit de inventario)`
                        : null;

                return (
                    <Box
                        key={`${line.order_request_id}-${line.product_id}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            py: 0.75,
                            opacity: line.state === 'complete' ? 0.5 : 1,
                        }}
                    >
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant={'body2'}>
                                {line.product_description}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                    alignItems: 'center',
                                    mt: 0.25,
                                }}
                            >
                                <Tooltip
                                    title={
                                        line.state === 'unmeasured'
                                            ? UNMEASURED_TOOLTIP
                                            : ''
                                    }
                                >
                                    <Chip
                                        size={'small'}
                                        color={STATE_COLOR[line.state]}
                                        variant={
                                            line.state === 'complete' ||
                                            line.state === 'unmeasured'
                                                ? 'outlined'
                                                : 'filled'
                                        }
                                        label={STATE_LABEL[line.state]}
                                        // Override the filled `warning` orange
                                        // with the golden yellow so it reads
                                        // apart from the red `none` chip.
                                        sx={
                                            line.state === 'partial'
                                                ? {
                                                      backgroundColor:
                                                          COVERAGE_PARTIAL_COLOR,
                                                      color: 'rgba(0,0,0,0.87)',
                                                  }
                                                : undefined
                                        }
                                    />
                                </Tooltip>
                                {planState.machines.length > 0 ? (
                                    <Chip
                                        size={'small'}
                                        variant={'outlined'}
                                        label={`planeado en ${planState.machines.join(
                                            ', ',
                                        )}`}
                                    />
                                ) : null}
                                {!tracked ? (
                                    <Tooltip
                                        title={
                                            'Este tipo de producción no registra consumo, así que no hay dato de inventario (pellet y compactado)'
                                        }
                                    >
                                        <Chip
                                            size={'small'}
                                            variant={'outlined'}
                                            label={'sin dato de inventario'}
                                        />
                                    </Tooltip>
                                ) : null}
                            </Box>
                            <Typography
                                variant={'caption'}
                                color={'text.secondary'}
                                sx={{ display: 'block', mt: 0.25 }}
                            >
                                {`Cantidad pendiente por surtir: ${quantity(
                                    line,
                                    line.remaining_kilos,
                                    line.remaining_groups,
                                )}`}
                            </Typography>
                            {inventorySummary || plannedSummary || realSummary ? (
                                <Typography
                                    variant={'caption'}
                                    color={'text.secondary'}
                                    sx={{ display: 'block' }}
                                >
                                    {[
                                        inventorySummary,
                                        plannedSummary,
                                        realSummary,
                                    ]
                                        .filter(
                                            (part): part is string =>
                                                part !== null,
                                        )
                                        .join(' · ')}
                                </Typography>
                            ) : null}
                            {line.state !== 'complete' ? (
                                <Typography
                                    variant={'caption'}
                                    color={'text.secondary'}
                                    sx={{ display: 'block' }}
                                >
                                    {[
                                        line.from_inventory_kilos > 0
                                            ? `asignado de inventario ${quantity(
                                                  line,
                                                  line.from_inventory_kilos,
                                                  line.from_inventory_groups,
                                              )}`
                                            : null,
                                        line.from_plan_kilos > 0
                                            ? `previsto este turno ${quantity(
                                                  line,
                                                  line.from_plan_kilos,
                                                  line.from_plan_groups,
                                              )}`
                                            : null,
                                        line.uncovered_kilos > 0
                                            ? `faltan ${quantity(
                                                  line,
                                                  line.uncovered_kilos,
                                                  line.uncovered_groups,
                                              )}`
                                            : null,
                                        productionNeededSummary,
                                    ]
                                        .filter((part) => part !== null)
                                        .join(' · ')}
                                </Typography>
                            ) : null}
                        </Box>

                        {showAdd ? (
                            <Tooltip
                                title={
                                    planState.canAdd
                                        ? 'Agregar a la planeación'
                                        : planState.reason || ''
                                }
                            >
                                {/* span so a disabled button still shows its
                                    tooltip — the reason is the point. */}
                                <span>
                                    <IconButton
                                        size={'small'}
                                        disabled={!planState.canAdd}
                                        onClick={() =>
                                            line.product_id &&
                                            onAdd(line.product_id)
                                        }
                                    >
                                        <AddCircleOutlineIcon
                                            fontSize={'inherit'}
                                        />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : null}
                    </Box>
                );
            })}
        </Paper>
    );
}

export function PedidosDrawer({
    open,
    onClose,
    pedidos,
    includePending,
    onIncludePendingChange,
    productPlanStates,
    plannedKilosByProduct,
    productionNeededByProduct,
    isInventoryTracked,
    ratesPending,
    canEdit,
    onAdd,
}: IPedidosDrawer) {
    return (
        <Drawer
            anchor={'right'}
            open={open}
            onClose={onClose}
            // NOT keepMounted, deliberately. The coverage allocation and the
            // per-product plan states are both rebuilt whenever the plan
            // changes; keeping ~150 lines mounted meant paying for that on every
            // keystroke anywhere on the page, for a drawer nobody was looking
            // at. Rebuilding on open is far cheaper than paying continuously.
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 600, md: 680 },
                    maxWidth: '100%',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                }}
            >
                <Typography variant={'h6'} sx={{ flexGrow: 1 }}>
                    Pedidos por surtir
                </Typography>
                <IconButton size={'small'} onClick={onClose}>
                    <CloseIcon fontSize={'inherit'} />
                </IconButton>
            </Box>
            <Divider />
            <Box sx={{ px: 2, pt: 1 }}>
                <FormControlLabel
                    control={
                        <Switch
                            size={'small'}
                            checked={includePending}
                            onChange={(e) =>
                                onIncludePendingChange(e.target.checked)
                            }
                        />
                    }
                    label={
                        <Typography variant={'body2'}>
                            Incluir pendientes
                        </Typography>
                    }
                />
            </Box>
            <Box sx={{ px: 2, pb: 2, overflow: 'auto' }}>
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    sx={{ display: 'block', mb: 1.5 }}
                >
                    El inventario se reparte entre{' '}
                    <strong>todos</strong> los pedidos por surtir, en el mismo
                    orden que esa página
                    {/* Said explicitly, because it is the one thing that makes
                        the numbers readable while the Pendientes are hidden: a
                        line covered for 20 of 60 was outranked by pedidos that
                        are NOT on screen. Without this the figure looks wrong. */}
                    {!includePending
                        ? ' — incluidos los pendientes que no se muestran aquí, que consumen inventario antes que los pedidos de menor prioridad'
                        : ''}
                    . &quot;Previsto este turno&quot; es lo que aporta la planeación en
                    pantalla, descontando lo que ya se capturó como producción
                    real en sus máquinas.
                </Typography>
                {ratesPending ? (
                    // The plan pool is still filling, so a line can read "sin
                    // cubrir" here and turn green a second later. Better to say
                    // the numbers are not final than to have them move silently.
                    <Alert severity={'info'} sx={{ mb: 1.5 }}>
                        Calculando el rendimiento de los productos planeados —
                        las cantidades &quot;previstas este turno&quot; todavía pueden
                        subir.
                    </Alert>
                ) : null}
                {pedidos.length === 0 ? (
                    <Typography color={'text.secondary'}>
                        {includePending
                            ? 'No hay pedidos pendientes por producir'
                            : 'No hay pedidos en producción. Activa "Incluir pendientes" para ver los demás.'}
                    </Typography>
                ) : (
                    pedidos.map((pedido) => (
                        <PedidoCard
                            key={pedido.order_request_id}
                            pedido={pedido}
                            productPlanStates={productPlanStates}
                            plannedKilosByProduct={plannedKilosByProduct}
                            productionNeededByProduct={
                                productionNeededByProduct
                            }
                            isInventoryTracked={isInventoryTracked}
                            canEdit={canEdit}
                            onAdd={onAdd}
                        />
                    ))
                )}
            </Box>
        </Drawer>
    );
}
